// Whiteboard — the zebras, in the Zebra logo's own look:
//   ZebraMark     the Zebra Innovation app mark (the zebra head with the blue
//                 "i") at the top of the tool rail
//   HardHatZebra  the logo's zebra head in a brand-blue hard hat — the button
//                 that brings the work item's drawings onto the board
//   ShopZebra     the whole zebra, side on (engine/zebra.js): it gallops,
//                 stands, and sits like a dog, wagging its tail; each pose is a
//                 smooth turn of its joints
//   ZebraRun      ShopZebra galloping from its button to the drawings pop-up,
//                 then handing over (onArrive) — skipped when the person's
//                 system asks for reduced motion

import React, { createElement, useId, useLayoutEffect, useRef, useState } from 'react';
import logoOnLight from '@app/assets/zebra-stride-logo-light.png';
import logoOnDark from '@app/assets/zebra-stride-logo.png';
import zebraMark from '@modules/database/whiteboard/frontend/assets/zebra-innovation-mark.png';
import { zebraTree, HARD_HAT, hatTransform } from '@modules/database/whiteboard/frontend/engine/zebra';

export function ZebraMark({ size = 30 }) {
  return <img className="wb-zmark" src={zebraMark} width={size} height={size} alt="" draggable={false} />;
}

// The logo's zebra head is the left 518 × 452 of the logo artwork (1400 × 452);
// the hat is drawn over it in the artwork's own pixels.
export function HardHatZebra({ size = 30 }) {
  const h = Math.round(size * 0.78); const w = Math.round((h * 518) / 452);
  const top = size - h; const left = Math.round((size - w) / 2);
  const k = 452 / h;   // artwork pixels per screen pixel
  const head = (src, tone) => (
    <span className={`wb-zbrand-img is-on-${tone}`} style={{ backgroundImage: `url(${src})`, left, top, width: w, height: h }} />
  );
  return (
    <span className="wb-hhz" style={{ width: size, height: size }} aria-hidden="true">
      {head(logoOnLight, 'light')}
      {head(logoOnDark, 'dark')}
      <svg className="wb-hhz-hat" width={size} height={size} viewBox={`${-left * k} ${-top * k} ${size * k} ${size * k}`}>
        <g transform={hatTransform()}>{HARD_HAT.map((p, i) => <path key={i} {...p} />)}</g>
      </svg>
    </span>
  );
}

// The zebra tree (engine/zebra.js) as React: each joint turns by its pose.
function toJsx(node, key) {
  if (node == null) return null;
  if (typeof node === 'string') return node;
  const props = { ...node.attrs, key };
  if (node.part) {
    const [r, x, y] = node.pose;
    props.style = { transformOrigin: `${node.pivot[0]}px ${node.pivot[1]}px`, transform: `translate(${x}px, ${y}px) rotate(${r}deg)` };
  }
  return createElement(node.tag, props, ...(node.children || []).map((c, i) => toJsx(c, i)));
}

// pose: 'standing' | 'sitting'; running gallops; perk = a quick look up (and a faster wag)
export function ShopZebra({ running = false, pose = 'standing', width = 110, perk = false }) {
  const id = useId().replace(/:/g, '');
  const tree = zebraTree({ id, pose, running });
  const svg = toJsx(tree, 'z');
  return createElement('svg', {
    ...svg.props, key: undefined, width, height: Math.round((width * 160) / 214), 'aria-hidden': true, focusable: 'false',
    className: `${svg.props.className}${perk ? ' is-perk' : ''}`,
  }, svg.props.children);
}
// Where the hooves stand, as a share of the drawing's height (from its top).
export const ZEBRA_FEET = (130 + 18) / 160;

const reduceMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

// from / to: { x, y } inside the board (the zebra's hooves land on `to`).
export function ZebraRun({ from, to, onArrive, width = 110 }) {
  const [phase, setPhase] = useState('start');
  const arrive = useRef(onArrive);
  arrive.current = onArrive;
  const H = (width * 160) / 214;
  const dist = Math.hypot(to.x - from.x, to.y - from.y);
  const ms = Math.round(Math.min(1050, Math.max(560, dist * 1.25)));
  const left = to.x < from.x;
  useLayoutEffect(() => {
    let live = true;
    const finish = () => { if (live) { live = false; arrive.current?.(); } };
    if (reduceMotion()) { finish(); return undefined; }
    // start the run on the next frame (a timer as well: a hidden tab paints no frames)
    const raf = requestAnimationFrame(() => setPhase('run'));
    const kick = setTimeout(() => setPhase('run'), 40);
    const t = setTimeout(finish, ms + 80);
    return () => { live = false; cancelAnimationFrame(raf); clearTimeout(kick); clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const at = phase === 'start' ? from : to;
  return (
    <div className="wbz-runner" style={{
      transform: `translate(${at.x - width / 2}px, ${at.y - H * ZEBRA_FEET}px)`,
      transition: phase === 'run' ? `transform ${ms}ms cubic-bezier(.32,.04,.32,1)` : 'none',
    }}>
      <div style={{ transform: left ? 'scaleX(-1)' : undefined }}><ShopZebra running width={width} /></div>
    </div>
  );
}
